import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './api'

export interface StaffUser {
  id: string
  email: string
  name: string
  roles: string[]
  mustChangePassword: boolean
}

/** Resolves to the signed-in user, or null when there is no session. */
export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<StaffUser | null> => {
    try {
      return (await api.get<{ user: StaffUser }>('/me')).user
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null
      throw err
    }
  },
  staleTime: 60_000,
})

export function useSignIn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<{ user: StaffUser }>('/auth/login', body),
    onSuccess: ({ user }) => qc.setQueryData(meQuery.queryKey, user),
  })
}

export function useChangePassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<{ user: StaffUser }>('/auth/change-password', body),
    onSuccess: ({ user }) => qc.setQueryData(meQuery.queryKey, user),
  })
}

export function useSignOut() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      qc.setQueryData(meQuery.queryKey, null)
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' })
    },
  })
}

export const firstName = (u: StaffUser) => u.name.split(' ')[0] ?? u.name

export function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')
}
